import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import PageSEO from "@/components/PageSEO";
import SiteScripts from "@/components/SiteScripts";
import TrackingPixels from "@/components/TrackingPixels";
import Home from "@/pages/Home";
import ProductListingPage from "@/pages/ProductListingPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import SparePartsPage from "@/pages/SparePartsPage";
import SparePartDetailPage from "@/pages/SparePartDetailPage";
import CheckoutPage from "@/pages/CheckoutPage";
import ROOnRentPage from "@/pages/ROOnRentPage";
import BlogListingPage from "@/pages/BlogListingPage";
import BlogDetailPage from "@/pages/BlogDetailPage";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import AccountPage from "@/pages/AccountPage";
import TeamPage from "@/pages/TeamPage";
import ComparePage from "@/pages/ComparePage";
import OrderSuccessPage from "@/pages/OrderSuccessPage";
import BookingSuccessPage from "@/pages/BookingSuccessPage";
import OrderTrackingPage from "@/pages/OrderTrackingPage";
import BookingTrackingPage from "@/pages/BookingTrackingPage";
import PolicyPage from "@/pages/PolicyPage";
import CMSPage from "@/pages/CMSPage";
import CartSidebar from "@/components/CartSidebar";
import WhatsAppButton from "@/components/WhatsAppButton";

// Admin Pages
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import ProductEditor from "@/pages/admin/ProductEditor";
import AdminPincodes from "@/pages/admin/AdminPincodes";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminServices from "@/pages/admin/AdminServices";
import AdminCMS from "@/pages/admin/AdminCMS";
import AdminSiteSettings from "@/pages/admin/AdminSiteSettings";
import AdminBusinessSettings from "@/pages/admin/AdminBusinessSettings";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import AdminBlogs from "@/pages/admin/AdminBlogs";
import AdminSpareParts from "@/pages/admin/AdminSpareParts";
import AdminHomepageShowcase from "@/pages/admin/AdminHomepageShowcase";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminWhatsAppTemplates from "@/pages/admin/AdminWhatsAppTemplates";
import AdminCityPages from "@/pages/admin/AdminCityPages";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminRentModels from "@/pages/admin/AdminRentModels";
import TermsAndConditionsPage from "@/pages/TermsAndConditionsPage";
import CityLandingPage from "@/pages/CityLandingPage";
import ServiceAreasPage from "@/pages/ServiceAreasPage";

// Router component that handles auth callback detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (auth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/products" element={<ProductListingPage />} />
      <Route path="/product/:id" element={<ProductDetailPage />} />
      <Route path="/spare-parts" element={<SparePartsPage />} />
      <Route path="/spare-part/:id" element={<SparePartDetailPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/ro-on-rent" element={<ROOnRentPage />} />
      <Route path="/blog" element={<BlogListingPage />} />
      <Route path="/blog/:slug" element={<BlogDetailPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/account" element={<AccountPage />} />
      <Route path="/team" element={<TeamPage />} />
      <Route path="/about" element={<CMSPage />} />
      <Route path="/contact" element={<CMSPage />} />
      <Route path="/services" element={<CMSPage />} />
      <Route path="/faq" element={<CMSPage />} />
      <Route path="/compare" element={<ComparePage />} />
      <Route path="/order-success/:orderId" element={<OrderSuccessPage />} />
      <Route path="/order-success" element={<OrderSuccessPage />} />
      <Route path="/booking-success/:bookingId" element={<BookingSuccessPage />} />
      <Route path="/booking-success" element={<BookingSuccessPage />} />
      
      {/* Public Tracking Pages */}
      <Route path="/track/order/:id" element={<OrderTrackingPage />} />
      <Route path="/track/order" element={<OrderTrackingPage />} />
      <Route path="/track/booking/:id" element={<BookingTrackingPage />} />
      <Route path="/track/booking" element={<BookingTrackingPage />} />
      
      {/* Policy Pages */}
      <Route path="/privacy-policy" element={<PolicyPage />} />
      <Route path="/refund-policy" element={<PolicyPage />} />
      <Route path="/terms-conditions" element={<PolicyPage />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
      <Route path="/darbhanga" element={<CityLandingPage slugProp="darbhanga" />} />
      <Route path="/city/:slug" element={<CityLandingPage />} />
      <Route path="/service-areas" element={<ServiceAreasPage />} />
      <Route path="/policy/:type" element={<PolicyPage />} />
      
      {/* Admin Login - Outside protected area */}
      <Route path="/admin/login" element={<AdminLogin />} />
      
      {/* Admin Routes - Protected */}
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="products/new" element={<ProductEditor />} />
        <Route path="products/:id/edit" element={<ProductEditor />} />
        <Route path="categories" element={<AdminCategories />} />
        <Route path="pincodes" element={<AdminPincodes />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="bookings" element={<AdminBookings />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="team" element={<AdminTeam />} />
        <Route path="services" element={<AdminServices />} />
        <Route path="cms" element={<AdminCMS />} />
        <Route path="settings" element={<AdminSiteSettings />} />
        <Route path="business-settings" element={<AdminBusinessSettings />} />
        <Route path="notifications" element={<AdminNotifications />} />
        <Route path="blogs" element={<AdminBlogs />} />
        <Route path="spare-parts" element={<AdminSpareParts />} />
        <Route path="homepage-showcase" element={<AdminHomepageShowcase />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="whatsapp-templates" element={<AdminWhatsAppTemplates />} />
        <Route path="city-pages" element={<AdminCityPages />} />
        <Route path="reviews" element={<AdminReviews />} />
        <Route path="rent-models" element={<AdminRentModels />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <AdminAuthProvider>
            <CartProvider>
              <BrowserRouter>
                <PageSEO />
                <SiteScripts />
                <TrackingPixels />
                <AppRouter />
                <CartSidebar />
                <WhatsAppButton />
              </BrowserRouter>
              <Toaster />
            </CartProvider>
          </AdminAuthProvider>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
